/**
 * Browser page translation (Google Translate in Chrome, Edge, Safari…) replaces text nodes with its own <font> elements.
 * React still holds the old nodes, so its next update calls removeChild / insertBefore on a node that is no longer there
 * and the whole page crashes ("Failed to execute 'insertBefore' on 'Node'…"). This is the widely used workaround
 * (facebook/react#11538): when the node has already been moved, skip the operation instead of throwing.
 * Visitors may translate ChemAI into any language (e.g. Arabic), so translation must not break the page.
 */
export function installTranslateGuard() {
  if (typeof Node !== 'function' || !Node.prototype) return;
  const proto = Node.prototype as Node & { __chemaiGuard?: boolean };
  if (proto.__chemaiGuard) return;
  proto.__chemaiGuard = true;
  const removeChild = proto.removeChild;
  proto.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) return child;
    return removeChild.call(this, child) as T;
  };
  const insertBefore = proto.insertBefore;
  proto.insertBefore = function <T extends Node>(this: Node, node: T, ref: Node | null): T {
    if (ref && ref.parentNode !== this) return insertBefore.call(this, node, null) as T;
    return insertBefore.call(this, node, ref) as T;
  };
}
